// ChatPanelLexical.tsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {createPortal, flushSync} from 'react-dom';
import {useChat} from './use-chat';
import type {
  Part,
  ChatMessage,
  ChatRole,
  ActionEvent,
} from 'src/components/agentic-ui';
import {useVirtualizer} from '@tanstack/react-virtual';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Button,
  SubTitleSmall,
  Chip,
  Input,
  CircularLoader,
} from '@spaced-out/ui-design-system';
import {
  Toast,
  ToastTitle,
  ToastBody,
  TOAST_SEMANTIC,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  Editor,
  type MentionUser,
  type UploadResult,
  type EditorHandle,
} from './editor';
import {
  type FlattenedPart,
  DefaultMessagePartView,
} from './default-message-view';
import {MinimizedChatButton} from './minimized-chat-button';
import {useFullscreenPortalStore} from './portal-to-ask-ai';
import {DockedRegion} from './docked-region';
import {useSubChatStatus} from './sub-chat-store';
import type {WidgetPart} from 'src/components/agentic-ui/types';
import {classify} from 'src/utils/classify';
import {useSpeechToText} from 'src/hooks/useSpeechToText';
import {RecordingActions} from './voice/recording-actions';
import {MicControl} from './voice/mic-control';
import css from './chat-panel.css';


export type {MentionUser, UploadResult} from './editor';
export type PanelMode =
  | 'floating'
  | 'attached'
  | 'fullscreen'
  | 'hidden'
  | 'minimized';

export interface ChatPanelProps {
  chatOptions: Parameters<typeof useChat>[0];
  fetchMentionSuggestions?: (query: string) => Promise<MentionUser[]>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
  headerSlot?: React.ReactNode;
  height?: number;
  placeholder?: string;
  dragVisibleHeaderHeight?: number;
  // Start screen configuration
  renderStartScreen?: (args: {
    publishMessageAction?: (action: ActionEvent) => void;
  }) => React.ReactNode;

  defaultMode?: PanelMode; // uncontrolled default
  defaultOpen?: boolean;
  onModeChange?: (m: PanelMode) => void;

  attachChatTo?: string | HTMLElement; // used when mode='attached'
  attachHeaderTo?: string | HTMLElement; // optional header container when mode='attached'
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  draggable?: boolean; // header-drag in floating mode
  initialPosition?: {x: number; y: number};
  positionPersistKey?: string; // optional persist of position
  messageHistory?: ChatMessage[];
  onSubmitFeedback?: (
    messageId: string,
    rating: number,
    comment: string,
  ) => Promise<void>;
  onReset?: () => unknown;
  panelLeftMargin?: number; // left margin value for side nav positioning
  elevation: number; // z-index to apply when panel is floating/minimized
  onClose?: () => unknown;
  // Past conversations view (in-panel)
  onHistoryClick?: () => void;
  showHistoryView?: boolean;
  onHistoryBack?: () => void;
  historyViewTitle?: string;
  historyViewContent?: React.ReactNode;
  conversationName?: string;
  onConversationNameChange?: (name: string) => void;
  onMessageSent?: () => void;
  // Voice-input lifecycle hooks for the consumer (e.g. analytics tracking).
  onVoiceInputStart?: () => void;
  onVoiceInputConfirm?: () => void;
  onVoiceInputCancel?: () => void;
}

export interface ChatPanelHandle {
  setMode: (m: PanelMode) => void;
  setOpen: (o: boolean) => void;
  setPosition: (p: {x: number; y: number}) => void;
  // Open the panel (switching out of 'hidden' if needed) and send a message
  openWithMessage: (parts: Part[]) => Promise<void>;
  currentMode?: PanelMode;
  /** Whether the panel is currently open (vs minimized). */
  currentOpen?: boolean;
  clearMessages: () => void;
  // Focus the composer editor (e.g. after returning from a docked sub-chat).
  focusComposer: () => void;
  updateMessageFeedback: (
    messageId: string,
    feedback_rating: number,
    feedback_comment: string,
  ) => unknown;
}

/* ================= Draggable header (floating mode) ================= */
// eslint-disable-next-line max-params
function useDraggableFloating(
  panelRef: React.RefObject<HTMLDivElement | null>,
  headerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  posState: {
    pos: {x: number; y: number};
    setPos: (p: {x: number; y: number}) => void;
  },
  persist?: {key?: string},
  visibleHeaderHeight: number = 20,
  minX: number = 0,
) {
  const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});
  const persistKey = persist?.key;

  // Use refs to avoid re-running effect when position changes during drag
  const posRef = useRef(posState.pos);
  const setPosRef = useRef(posState.setPos);
  // Update refs during render (not in effect) to always have latest values
  posRef.current = posState.pos;
  setPosRef.current = posState.setPos;

  const save = useCallback(
    (p: {x: number; y: number}) => {
      if (!persistKey) {
        return;
      }
      localStorage.setItem(persistKey, JSON.stringify(p));
    },
    [persistKey],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const panel = panelRef.current;
    const header = headerRef.current;
    if (!panel || !header) {
      return;
    }

    const ro = new ResizeObserver(() => {
      const rect = panel.getBoundingClientRect();
      sizeRef.current = {w: rect.width, h: rect.height};
    });
    ro.observe(panel);

    let dragging = false;
    let startX = 0,
      startY = 0;
    let originX = 0,
      originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (
        (e.target as HTMLElement).closest(
          'button, a, input, textarea, [contenteditable="true"]',
        )
      ) {
        return;
      }
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const rect = panel.getBoundingClientRect();
      originX = rect.left;
      originY = rect.top;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) {
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const next = clampToViewport(
        {x: originX + dx, y: originY + dy},
        sizeRef.current,
        visibleHeaderHeight,
        minX,
      );
      setPosRef.current(next);
      posRef.current = next;
    };
    const onPointerUp = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };
    const onPointerCancel = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };

    header.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      header.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      ro.disconnect();
    };
  }, [enabled, panelRef, headerRef, save, visibleHeaderHeight, minX]);
}

function clampToViewport(
  p: {x: number; y: number},
  size: {w: number; h: number},
  visibleHeaderHeight?: number,
  minX: number = 0,
) {
  const vw = window.innerWidth,
    vh = window.innerHeight;
  const maxX = Math.max(minX, vw - size.w);
  // Allow the panel to overflow the bottom so only the header remains visible
  const headerH = Math.max(0, visibleHeaderHeight ?? size.h);
  const maxY = Math.max(0, vh - headerH);
  return {
    x: Math.min(Math.max(minX, p.x), maxX),
    y: Math.min(Math.max(0, p.y), maxY),
  };
}

// Manage the minimized button's resting Y coordinate. Persists to localStorage,
// clamps a stale persisted value into the current viewport on mount, and
// re-clamps on viewport resize/zoom so the button never renders off-screen.
function usePersistedMinimizedY(
  panelRef: React.RefObject<HTMLDivElement | null>,
  minimized: boolean,
  positionPersistKey?: string,
) {
  const storageKey = positionPersistKey
    ? `${positionPersistKey}:minimizedY`
    : undefined;

  const [minimizedY, setMinimizedY] = useState<number | null>(() => {
    if (!storageKey) {
      return null;
    }
    try {
      const v = localStorage.getItem(storageKey);
      if (!v) {
        return null;
      }
      const n = parseFloat(v);
      if (!Number.isFinite(n)) {
        return null;
      }
      // 40px reserve approximates the button height — refined once measured.
      if (typeof window !== 'undefined') {
        const maxY = Math.max(0, window.innerHeight - 40);
        return Math.min(Math.max(0, n), maxY);
      }
      return n;
    } catch {
      return null;
    }
  });

  const saveMinimizedY = useCallback(
    (y: number) => {
      if (!storageKey) {
        return;
      }
      try {
        localStorage.setItem(storageKey, String(y));
      } catch {}
    },
    [storageKey],
  );

  // Initialize from current docked location (so switching from `bottom` to
  // `top` doesn't jump), and re-clamp persisted Y against the measured height.
  useLayoutEffect(() => {
    if (!minimized) {
      return;
    }
    const panelEl = panelRef.current;
    if (!panelEl) {
      return;
    }
    const rect = panelEl.getBoundingClientRect();
    if (!Number.isFinite(rect.top) || !Number.isFinite(rect.height)) {
      return;
    }
    if (minimizedY === null) {
      setMinimizedY(rect.top);
      return;
    }
    const maxY = Math.max(0, window.innerHeight - rect.height);
    const clamped = Math.min(Math.max(0, minimizedY), maxY);
    if (clamped !== minimizedY) {
      setMinimizedY(clamped);
      saveMinimizedY(clamped);
    }
  }, [minimized, minimizedY, saveMinimizedY, panelRef]);

  useEffect(() => {
    if (!minimized) {
      return;
    }
    const onResize = () => {
      const panelEl = panelRef.current;
      if (!panelEl) {
        return;
      }
      const rect = panelEl.getBoundingClientRect();
      if (!Number.isFinite(rect.height)) {
        return;
      }
      setMinimizedY((prev) => {
        if (prev === null) {
          return prev;
        }
        const maxY = Math.max(0, window.innerHeight - rect.height);
        const clamped = Math.min(Math.max(0, prev), maxY);
        if (clamped === prev) {
          return prev;
        }
        saveMinimizedY(clamped);
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minimized, saveMinimizedY, panelRef]);

  return {minimizedY, setMinimizedY, saveMinimizedY};
}

function useDraggableMinimized(
  panelRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  args: {
    dragPos: {x: number; y: number} | null;
    setDragPos: (p: {x: number; y: number} | null) => void;
    setRestingY: (y: number) => void;
    persistY?: (y: number) => void;
    didDragRef: React.MutableRefObject<boolean>;
  },
  minX: number = 0,
) {
  const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});

  // Keep latest setters in refs so pointermove doesn't rebind
  const dragPosRef = useRef(args.dragPos);
  const setDragPosRef = useRef(args.setDragPos);
  const setRestingYRef = useRef(args.setRestingY);
  const persistYRef = useRef(args.persistY);
  const didDragRef = args.didDragRef;

  dragPosRef.current = args.dragPos;
  setDragPosRef.current = args.setDragPos;
  setRestingYRef.current = args.setRestingY;
  persistYRef.current = args.persistY;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const DRAG_THRESHOLD_PX = 5;
    let dragging = false;
    let startY = 0;
    let originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      didDragRef.current = false;
      startY = e.clientY;
      const rect = panel.getBoundingClientRect();
      // Capture current size for viewport clamping during this drag.
      sizeRef.current = {w: rect.width, h: rect.height};
      originY = rect.top;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) {
        return;
      }
      const dy = e.clientY - startY;
      if (!didDragRef.current) {
        if (Math.abs(dy) >= DRAG_THRESHOLD_PX) {
          didDragRef.current = true;
        }
      }
      const next = clampToViewport(
        {x: minX, y: originY + dy},
        sizeRef.current,
        undefined,
        minX,
      );
      setDragPosRef.current(next);
      dragPosRef.current = next;
    };
    const onPointerUp = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      const p = dragPosRef.current;
      setDragPosRef.current(null);
      dragPosRef.current = null;

      // Only commit if the user actually dragged (avoid changing Y on click)
      if (p && didDragRef.current) {
        setRestingYRef.current(p.y);
        persistYRef.current?.(p.y);
      }
    };
    const onPointerCancel = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      setDragPosRef.current(null);
      dragPosRef.current = null;
    };

    panel.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      panel.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [enabled, panelRef, didDragRef, minX]);
}

function getPanelStyle({
  imode,
  minimized,
  floatingFullHeight,
  height,
  pos,
  hasCustomPosition,
  panelLeftMargin,
  minimizedY,
  minimizedDragPos,
  elevation,
}: {
  imode: PanelMode;
  minimized: boolean;
  floatingFullHeight: boolean;
  height: number;
  pos: {x: number; y: number};
  hasCustomPosition: boolean;
  panelLeftMargin?: number;
  minimizedY: number | null;
  minimizedDragPos: {x: number; y: number} | null;
  elevation: number;
}): React.CSSProperties | undefined {
  const isFloating = imode === 'floating';
  const isFullscreen = imode === 'fullscreen';
  const dockLeft = panelLeftMargin ?? 0;

  let left: number | undefined;
  if (isFloating || minimized) {
    if (hasCustomPosition) {
      left = pos.x; // user dragged, use their position
    } else if (panelLeftMargin !== undefined) {
      left = panelLeftMargin; // use provided side nav margin
    }
  }

  if (minimized) {
    // While dragging: keep dock X fixed and only move vertically.
    if (minimizedDragPos) {
      return {
        left: dockLeft,
        top: minimizedDragPos.y,
        bottom: 'auto',
        zIndex: elevation,
      } as React.CSSProperties;
    }
    // Resting minimized position: keep dock X, but apply stored Y (top) if present.
    if (minimizedY !== null) {
      return {
        left: dockLeft,
        top: minimizedY,
        bottom: 'auto',
        zIndex: elevation,
      } as React.CSSProperties;
    }
    return {
      left: dockLeft,
      bottom: 8,
      zIndex: elevation,
    } as React.CSSProperties;
  }
  return {
    minHeight: isFullscreen
      ? '100%'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    maxHeight: isFullscreen
      ? '100%'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    width: isFloating ? 420 : '100%',
    position: isFloating ? ('fixed' as const) : ('relative' as const),
    left,
    top: isFloating && hasCustomPosition ? pos.y : undefined,
    zIndex:
      imode === 'floating' ? elevation : imode !== 'attached' ? 60 : 'auto',
  } as React.CSSProperties;
}

/* ================= Header subcomponents ================= */
type TFunction = (key: string, fallback: string) => string;

interface ChatPanelHeaderControlsProps {
  t: TFunction;
  imode: PanelMode;
  onMinimize: () => void;
  showCloseButton: boolean;
  onClose: () => void;
  showHistoryButton?: boolean;
  onHistoryClick?: () => void;
}

function ChatPanelHeaderControls({
  t,
  imode,
  onMinimize,
  showCloseButton,
  onClose,
  showHistoryButton,
  onHistoryClick,
}: ChatPanelHeaderControlsProps) {
  return (
    <div className={css.headerControls}>
      {showHistoryButton && onHistoryClick ? (
        <Button
          type="ghost"
          iconLeftName="history"
          size="small"
          onClick={onHistoryClick}
          aria-label={t('Past_conversations', 'Past conversations')}
        />
      ) : null}
      {imode !== 'fullscreen' ? (
        <Button
          type="ghost"
          iconLeftName="minus"
          size="small"
          onClick={onMinimize}
          aria-label={t('Minimize', 'Minimize')}
        />
      ) : null}
      {showCloseButton ? (
        <Button
          type="ghost"
          size="small"
          iconLeftName="close"
          onClick={onClose}
          aria-label={t('Close', 'Close')}
        />
      ) : null}
    </div>
  );
}

interface ChatPanelHistoryHeaderProps {
  t: TFunction;
  historyViewTitle?: string;
  showBackButton: boolean;
  onHistoryBack?: () => void;
  controlsProps: Omit<
    ChatPanelHeaderControlsProps,
    'showHistoryButton' | 'onHistoryClick'
  >;
}

function ChatPanelHistoryHeader({
  t,
  historyViewTitle,
  onHistoryBack,
  controlsProps,
}: ChatPanelHistoryHeaderProps) {
  return (
    <>
      <div className={css.headerNameAndBackButtonContainer}>
        <Button
          type="ghost"
          iconLeftName="chevron-left"
          size="small"
          onClick={onHistoryBack}
          aria-label={t('Back', 'Back')}
        />
        <SubTitleSmall>{historyViewTitle}</SubTitleSmall>
      </div>
      <ChatPanelHeaderControls {...controlsProps} />
    </>
  );
}

interface ChatPanelChatHeaderProps {
  t: TFunction;
  showBackButton: boolean;
  showHistoryButton: boolean;
  onReset: () => void;
  headerSlot?: React.ReactNode;
  imode: PanelMode;
  onHistoryClick?: () => void;
  controlsProps: ChatPanelHeaderControlsProps;
  conversationName?: string;
  onConversationNameChange?: (name: string) => void;
}

function ChatPanelChatHeader({
  t,
  showBackButton,
  showHistoryButton,
  onReset,
  headerSlot,
  imode,
  onHistoryClick,
  controlsProps,
  conversationName,
  onConversationNameChange,
}: ChatPanelChatHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleStartEditing = useCallback(() => {
    setEditValue(conversationName ?? '');
    setIsEditing(true);
  }, [conversationName]);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== conversationName) {
      onConversationNameChange?.(trimmed);
    }
  }, [editValue, conversationName, onConversationNameChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inputRef.current?.blur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <>
      <div className={css.headerNameAndBackButtonContainer}>
        {showBackButton && (
          <Button
            type="ghost"
            iconLeftName="chevron-left"
            size="small"
            onClick={onReset}
          />
        )}
        {conversationName != null ? (
          isEditing ? (
            <div className={css.conversationNameInputWrapper}>
              <Input
                ref={inputRef}
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.currentTarget.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
              />
            </div>
          ) : (
            <div
              className={css.conversationNameText}
              onClick={handleStartEditing}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleStartEditing();
                }
              }}
            >
              <SubTitleSmall className={css.conversationNameEllipsis}>
                {conversationName}
              </SubTitleSmall>
            </div>
          )
        ) : (
          <>
            <SubTitleSmall>{t('Sense_IQ', 'Sense IQ')}</SubTitleSmall>
            <Chip size="small">{t('Beta', 'Beta')}</Chip>
          </>
        )}
        {imode === 'attached' ? headerSlot : null}
      </div>
      <ChatPanelHeaderControls
        {...controlsProps}
        showHistoryButton={showHistoryButton}
        onHistoryClick={onHistoryClick}
      />
    </>
  );
}

/* ================= Main Panel ================= */
export const ChatPanelLexical = forwardRef<ChatPanelHandle, ChatPanelProps>(
  (
    {
      chatOptions,
      // fetchMentionSuggestions intentionally unused — @-mentions not wired yet.
      onUploadFiles,
      height = 660,
      placeholder,
      dragVisibleHeaderHeight = 20,
      defaultMode = 'hidden',
      onModeChange,
      attachChatTo,
      attachHeaderTo,
      headerSlot,
      defaultOpen = true,
      onOpenChange,
      draggable = true,
      initialPosition = {x: 16, y: 16},
      positionPersistKey = 'chatPos',
      messageHistory = [],
      onSubmitFeedback,
      renderStartScreen,
      onReset,
      onClose,
      panelLeftMargin,
      elevation,
      onHistoryClick,
      showHistoryView = false,
      onHistoryBack,
      historyViewTitle,
      historyViewContent,
      conversationName,
      onConversationNameChange,
      onMessageSent,
      onVoiceInputStart,
      onVoiceInputConfirm,
      onVoiceInputCancel,
    },
    ref,
  ) => {
    const {t} = useLabelTranslation();
    const chat = useChat(chatOptions);
    // Groups the user has explicitly toggled, i.e. deviated from the default.
    // Default expansion is "open only the last (most recent) side conversation;
    // older ones start collapsed". Membership here flips that default for a
    // given group, so a tap opens an old group or closes the latest one.
    const [userToggledGroups, setUserToggledGroups] = useState<Set<string>>(
      () => new Set(),
    );
    const [minimizedDragPos, setMinimizedDragPos] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const minimizedDidDragRef = useRef(false);
    const [imode, setIMode] = useState<PanelMode>(defaultMode);

    const setModeSafe = useCallback(
      (m: PanelMode) => {
        setIMode(m);
        onModeChange?.(m);
      },
      [onModeChange],
    );

    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
    const minimized = !isOpen;
    // In fullscreen, an asset occupying the portal switches the shell from a
    // centered conversation to the two-pane (conversation + asset) layout.
    const hasFullscreenAsset = useFullscreenPortalStore((s) => s.count > 0);
    // When the sub-chat window is maximized, the rest of the page is locked
    // behind a backdrop so only the panel is interactive.
    const subChatStatus = useSubChatStatus();
    const editorRef = useRef<EditorHandle | null>(null);

    // Streaming speech-to-text: mic → provider WebSocket → interim (faded) +
    // final (corrected) transcript written live into the composer.
    const stt = useSpeechToText({
      onInterim: (text) => editorRef.current?.pushInterim(text),
      onFinal: (text) => editorRef.current?.pushFinal(text),
      // Capture/connection failures surface as a danger toast, fired at the
      // error event. Permission denial is excluded (pending UX).
      onError: (err) => {
        if (err.code === 'permission-denied') {
          return;
        }
        const noMic = err.code === 'no-microphone';
        const title = noMic
          ? t('No_microphone_found', 'No microphone found')
          : t('Microphone_Connection_Failed', 'Microphone Connection Failed');
        const body = noMic
          ? t(
              'Connect_a_microphone_and_try_again',
              'Connect a microphone and try again',
            )
          : t(
              'Selected_microphone_cannot_be_connected_Use_other_microphone_or_try_again',
              'Selected microphone cannot be connected. Use other microphone or try again',
            );
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{title}</ToastTitle>
            <ToastBody>{body}</ToastBody>
          </Toast>,
          {timeout: 6000},
        );
      },
    });

    // list + scroll
    const scrollerRef = useRef<HTMLDivElement | null>(null);

    // One DOM row per flattened part
    const rowRefs = useRef(new Map<string, HTMLDivElement | null>());

    // A synthetic row that heads a collapsed run of MIN side-questions.
    type MinGroupHeaderRow = {
      kind: 'min-group-header';
      groupId: string;
      label: string;
      collapsed: boolean;
    };
    type Row = FlattenedPart | MinGroupHeaderRow;

    const rows: Row[] = useMemo(() => {
      // History (server) and the local streaming store can both hold the same
      // message (same id) — without dedup it renders twice. The live local copy
      // is the source of truth for anything sent/streamed in this session: it is
      // always at least as fresh as history and is the ONLY copy that reflects an
      // in-flight stream token-by-token. History, by contrast, is refetched
      // mid-session (e.g. the 5+-message invalidation in ask-ai's
      // `handleMessageSent`) and that refetch can land while a message is still
      // streaming, returning an empty/partial copy. So when a message exists in
      // both, prefer the LOCAL copy and let history supply only the messages
      // absent from the local store (older / previous-session messages).
      // Preferring history here was the bug: a stale history fetch shadowed the
      // live streamed message, so it stopped rendering until a page refresh.
      const localIdSet = new Set(chat.snapshot.messages.map((m) => m.id));
      const allMessages: ChatMessage[] = [
        ...messageHistory.filter((h) => !localIdSet.has(h.id)),
        ...chat.snapshot.messages,
      ];

      const flattenMsg = (msg: ChatMessage): FlattenedPart[] => {
        const kept = msg.parts.filter(
          (part) =>
            !(
              part.type === 'widget' &&
              (part as WidgetPart).data.widget_visibility === 'docked'
            ),
        );
        return kept.map((part, index) => ({
          ...part,
          messageId: msg.id,
          messageRole: msg.role,
          showAssistantAnchor: msg.role === 'assistant',
          isLastPartInMessage: index === kept.length - 1,
          feedbackRating: msg.feedback_rating,
          feedbackComment: msg.feedback_comment,
          subChatWindowStatus: msg.sub_chat_window_status,
        }));
      };

      // First pass: split the transcript into inline messages and side-
      // conversation groups. A group begins at the first message sent while
      // minimized and absorbs everything that follows — including assistant
      // replies, whose status may be 'absent'/undefined — until the session is
      // closed by a MAX message (submit or abandon). That closing MAX is NOT
      // part of the group; it falls through inline. Stopping at the first 'max'
      // (instead of the first non-'min') keeps one side conversation as a single
      // group rather than splitting it at every assistant answer.
      type Segment =
        | {type: 'inline'; msg: ChatMessage}
        | {type: 'group'; groupId: string; label: string; run: ChatMessage[]};

      const segments: Segment[] = [];
      let i = 0;
      while (i < allMessages.length) {
        const m = allMessages[i];
        if (m.sub_chat_window_status === 'min') {
          const runStart = i;
          const run: ChatMessage[] = [];
          while (
            i < allMessages.length &&
            allMessages[i].sub_chat_window_status !== 'max'
          ) {
            run.push(allMessages[i]);
            i += 1;
          }
          const firstUserText = run
            .find((rm) => rm.role === 'user')
            ?.parts.find((p) => p.type === 'plain_text') as
            | {data: string}
            | undefined;
          const baseLabel = (
            firstUserText?.data ?? t('Side_conversation', 'Side conversation')
          ).slice(0, 40);
          segments.push({
            type: 'group',
            groupId: allMessages[runStart].id,
            label: `${baseLabel} · ${run.length}`,
            run,
          });
        } else {
          segments.push({type: 'inline', msg: m});
          i += 1;
        }
      }

      // Default: only the last (most recent) side conversation is open; older
      // ones start collapsed. `userToggledGroups` flips that default per group.
      let lastGroupId: string | null = null;
      for (const seg of segments) {
        if (seg.type === 'group') {
          lastGroupId = seg.groupId;
        }
      }

      const out: Row[] = [];
      for (const seg of segments) {
        if (seg.type === 'inline') {
          out.push(...flattenMsg(seg.msg));
          continue;
        }
        const isLast = seg.groupId === lastGroupId;
        const expanded = userToggledGroups.has(seg.groupId) ? !isLast : isLast;
        out.push({
          kind: 'min-group-header',
          groupId: seg.groupId,
          label: seg.label,
          collapsed: !expanded,
        });
        if (expanded) {
          for (const rm of seg.run) {
            out.push(...flattenMsg(rm));
          }
        }
      }
      return out;
    }, [messageHistory, chat.snapshot.messages, userToggledGroups, t]);

    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => scrollerRef.current,
      estimateSize: () => 120,
      overscan: 8,
    });

    const [autoScroll, setAutoScroll] = useState(true);
    const autoScrollRef = useRef(true);
    autoScrollRef.current = autoScroll;

    const onScroll = useCallback<React.UIEventHandler<HTMLDivElement>>((e) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      const NEAR_BOTTOM = 40;
      const shouldStick = distanceFromBottom < NEAR_BOTTOM;

      if (shouldStick !== autoScrollRef.current) {
        autoScrollRef.current = shouldStick;
        setAutoScroll(shouldStick);
      }
    }, []);

    // scroll to last part when:
    // - rows grow (new parts/messages)
    // - measurement changes (dynamic height)

    useLayoutEffect(() => {
      if (!autoScrollRef.current || rows.length === 0) {
        return;
      }

      virtualizer.scrollToIndex(rows.length - 1, {align: 'end'});
    }, [rows.length, virtualizer]);

    // Callback for widgets to notify when they resize
    // Widgets call this after their own timing logic (animations/transitions)
    // We just measure the full row height immediately
    const handleWidgetResize = useCallback(
      (rowKey: string) => {
        const rowEl = rowRefs.current.get(rowKey);
        if (!rowEl) {
          return;
        }

        requestAnimationFrame(() => {
          // Re-measure this row because the widget changed size
          virtualizer.measureElement(rowEl);
          virtualizer.measure();

          // If user is "stuck to bottom", keep view pinned to last part
          if (autoScrollRef.current && rows.length > 0) {
            virtualizer.scrollToIndex(rows.length - 1, {align: 'end'});
          }
        });
      },
      [virtualizer, rows.length],
    );

    const onSendUserMessage = useCallback(
      async (parts: Part[]) => {
        autoScrollRef.current = true;
        setAutoScroll(true);
        await chat.sendMessage(parts);
        onMessageSent?.();
      },
      [chat, onMessageSent],
    );

    // header + drag
    const panelRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    // Lazy initialization: read persisted position or use initialPosition
    const [pos, setPos] = useState<{x: number; y: number}>(() => {
      if (!positionPersistKey) {
        return initialPosition;
      }
      const read = () => {
        const v = localStorage.getItem(positionPersistKey);
        if (!v) {
          return null;
        }
        try {
          return JSON.parse(v) as {x: number; y: number};
        } catch {
          return null;
        }
      };
      return read() || initialPosition;
    });

    const hasCustomPositionRef = useRef(
      positionPersistKey ? !!localStorage.getItem(positionPersistKey) : false,
    );

    // Minimized button Y (persisted separately; X always snaps to dock X)
    const {minimizedY, setMinimizedY, saveMinimizedY} = usePersistedMinimizedY(
      panelRef,
      minimized,
      positionPersistKey,
    );

    // track when user drags to set custom position
    const handleSetPos = useCallback((newPos: {x: number; y: number}) => {
      hasCustomPositionRef.current = true;
      setPos(newPos);
    }, []);

    const [floatingFullHeight, _setFloatingFullHeight] =
      useState<boolean>(true);
    useDraggableFloating(
      panelRef,
      headerRef,
      draggable && imode === 'floating' && isOpen,
      {pos, setPos: handleSetPos},
      //NOTE:(diwakersurya)we are not persisting the position of the chat panel in the local storage for now.
      //{key: positionPersistKey},
      undefined,
      dragVisibleHeaderHeight,
      panelLeftMargin ?? 0,
    );

    // Dragging the minimized button (free drag while dragging, snap X on release)
    useDraggableMinimized(
      panelRef,
      minimized,
      {
        dragPos: minimizedDragPos,
        setDragPos: setMinimizedDragPos,
        setRestingY: setMinimizedY,
        persistY: saveMinimizedY,
        didDragRef: minimizedDidDragRef,
      },
      panelLeftMargin ?? 0,
    );

    // container for attached mode - compute during render instead of effect
    const container =
      imode === 'attached'
        ? attachChatTo instanceof HTMLElement
          ? attachChatTo
          : typeof attachChatTo === 'string'
          ? (document.querySelector(attachChatTo) as HTMLElement | null)
          : null
        : null;

    const headerContainer =
      imode === 'attached'
        ? attachHeaderTo instanceof HTMLElement
          ? attachHeaderTo
          : typeof attachHeaderTo === 'string'
          ? (document.querySelector(attachHeaderTo) as HTMLElement | null)
          : null
        : null;

    // expose imperative API
    useImperativeHandle(
      ref,
      () => ({
        setMode: setModeSafe,
        setOpen: (o: boolean) =>
          onOpenChange ? onOpenChange(o) : setIsOpen(o),
        setPosition: setPos,
        clearMessages: () => {
          chat.clear();
        },
        openWithMessage: async (parts: Part[]) => {
          if (!isOpen) {
            if (onOpenChange) {
              onOpenChange(true);
            } else {
              setIsOpen(true);
            }
          }

          await chat.sendMessage(parts);
          // If hidden, reveal as floating by default
          if (imode === 'hidden') {
            setModeSafe('floating');
          }
        },
        updateMessageFeedback: (
          messageId: string,
          feedback_rating: number,
          feedback_comment: string,
        ) => {
          chat.updateMessage(messageId, {feedback_rating, feedback_comment});
        },
        focusComposer: () => {
          // Defer to after the composer remounts (sub-chat reset → ABSENT).
          requestAnimationFrame(() => editorRef.current?.focus());
        },

        currentMode: imode,
        currentOpen: isOpen,
      }),
      [setModeSafe, onOpenChange, imode, isOpen, chat],
    );

    // Hidden mode: render nothing at all (no button)
    if (imode === 'hidden') {
      return null;
    }

    const shouldShowCloseButton = isOpen && imode === 'fullscreen';

    const startContent = renderStartScreen?.({
      publishMessageAction: chatOptions.publishMessageAction,
    });
    const handleReset = () => {
      hasCustomPositionRef.current = false;
      chat.clear();
      onReset?.();
    };
    const showStartScreen =
      startContent !== null && chat.snapshot.messages.length === 0;
    const isOnInitialPage = showStartScreen && !showHistoryView;

    const headerControlsProps = {
      t,
      imode,
      onMinimize: () => setIsOpen(false),
      showCloseButton: shouldShowCloseButton,
      onClose: () => {
        // The close (X) only shows in fullscreen. When a host owns the close
        // (AskAI navigates back to sourceurl and restores floating mode),
        // defer to it — minimizing here would leave the bubble collapsed on
        // return. Only minimize as a fallback when no host handler exists.
        if (onClose) {
          onClose();
        } else {
          setIsOpen(false);
        }
      },
    };

    const headerContent = showHistoryView ? (
      <ChatPanelHistoryHeader
        t={t}
        showBackButton={true}
        historyViewTitle={historyViewTitle}
        onHistoryBack={onHistoryBack}
        controlsProps={headerControlsProps}
      />
    ) : (
      <ChatPanelChatHeader
        t={t}
        showBackButton={!isOnInitialPage && imode !== 'fullscreen'}
        showHistoryButton={imode !== 'fullscreen' && Boolean(onHistoryClick)}
        onReset={handleReset}
        headerSlot={headerSlot}
        imode={imode}
        onHistoryClick={onHistoryClick}
        controlsProps={headerControlsProps}
        conversationName={isOnInitialPage ? undefined : conversationName}
        onConversationNameChange={
          isOnInitialPage ? undefined : onConversationNameChange
        }
      />
    );

    const header = (
      <header
        ref={headerRef}
        className={classify(css.header, {
          [css.headerNoDrag]: minimized || imode !== 'floating',
        })}
      >
        {headerContent}
      </header>
    );

    const body = showHistoryView ? (
      <div className={css.historyViewContent}>{historyViewContent}</div>
    ) : (
      <>
        <div className={css.scrollerWrap}>
          {/* While the sub-chat is maximized the conversation above is locked —
              a transparent overlay captures pointer events so only the docked
              region below stays interactive. */}
          {subChatStatus === 'max' && (
            <div
              className={css.subChatBackdrop}
              // Match the panel's stacking (getPanelStyle uses `elevation`) so
              // the overlay sits above the conversation it locks.
              style={{zIndex: elevation}}
              aria-hidden="true"
            />
          )}
          <div
            ref={scrollerRef}
            className={css.scroller}
            onScroll={onScroll}
            // Keyboard/AT equivalent of the pointer backdrop above: while the
            // sub-chat is maximized the conversation is non-interactive.
            inert={subChatStatus === 'max'}
          >
            {showStartScreen ? (
              <div className={css.startScreenContainer}>{startContent}</div>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((item) => {
                  const row = rows[item.index];
                  if (!row) {
                    return null;
                  }

                  if ('kind' in row && row.kind === 'min-group-header') {
                    const collapsed = row.collapsed;
                    return (
                      <div
                        data-index={item.index}
                        key={`min-group:${row.groupId}`}
                        ref={(el) => {
                          if (el) {
                            virtualizer.measureElement(el);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${item.start}px)`,
                        }}
                      >
                        <Button
                          type="ghost"
                          size="small"
                          iconLeftName={
                            collapsed ? 'angle-right' : 'angle-down'
                          }
                          onClick={() =>
                            setUserToggledGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(row.groupId)) {
                                next.delete(row.groupId);
                              } else {
                                next.add(row.groupId);
                              }
                              return next;
                            })
                          }
                        >
                          {row.label}
                        </Button>
                      </div>
                    );
                  }

                  const part = row as FlattenedPart;
                  const rowKey = `${part.messageId}:${part.id ?? item.index}`;

                  return (
                    <div
                      data-index={item.index}
                      key={rowKey}
                      ref={(el) => {
                        // keep ref map in sync
                        if (!el) {
                          rowRefs.current.delete(rowKey);
                        } else {
                          rowRefs.current.set(rowKey, el);
                          virtualizer.measureElement(el);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${item.start}px)`,
                      }}
                    >
                      <DefaultMessagePartView
                        part={part}
                        role={part.messageRole}
                        publishMessageAction={chatOptions.publishMessageAction}
                        onWidgetResize={() => handleWidgetResize(rowKey)}
                        showChatBubble={true}
                        showAssistantAnchor={part.showAssistantAnchor}
                        onSubmitFeedback={onSubmitFeedback}
                        //don't show feedback component if this is last message and server is still responding to this message
                        showFeedback={
                          item.index !== rows.length - 1 ||
                          !chat.isServerResponseInProgress
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DockedRegion
          serverResponding={chat.isServerResponseInProgress}
          onSend={onSendUserMessage}
          publishMessageAction={chatOptions.publishMessageAction}
          composer={
            <footer className={css.footer}>
              {/* On record, the Editor switches to recording mode: the live
                  transcript streams into the text input, the placeholder shows
                  the status ("Connecting…"/"Listening…") while it's empty, and
                  the bottom bar swaps in a spinner (connecting) or ✕/✓
                  (recording). The Editor stays mounted so its ref + typed text
                  survive. */}
              <Editor
                ref={editorRef}
                elevation={elevation}
                placeholder={
                  stt.state === 'connecting'
                    ? t('Connecting', 'Connecting...')
                    : stt.state === 'recording' || stt.state === 'stopping'
                    ? t('Listening', 'Listening...')
                    : placeholder
                }
                onUploadFiles={onUploadFiles}
                onSend={onSendUserMessage}
                disableSend={chat.snapshot.connectionStatus === 'OPEN'}
                autoFocus={isOpen}
                // Two-row layout (text above a full-width action row) so the
                // action bar has room for the mic device picker + record/send.
                // @-mentions intentionally not wired yet (no fetcher passed).
                showAttachmentButton={true}
                isRecording={stt.state !== 'idle'}
                micControl={
                  stt.state === 'idle' ? (
                    <MicControl
                      permission={stt.permission}
                      devices={stt.devices}
                      selectedDeviceId={stt.selectedDeviceId}
                      onSelectDevice={stt.setSelectedDeviceId}
                      micDisabled={chat.snapshot.connectionStatus === 'OPEN'}
                      micLabel={t('Start_voice_input', 'Start voice input')}
                      defaultMicLabel={t(
                        'Default_Microphone',
                        'Default Microphone',
                      )}
                      micPromptTooltip={t(
                        'We\'ll_ask_for_mic_access_please_click_Allow_to_start_speechtotext',
                        '🎙️ We\'ll ask for mic access — please click "Allow" to start speech-to-text.',
                      )}
                      micDeniedTooltip={t(
                        'Mic_access_blocked_Enable_it_in_your_browser\'s_site_settings_to_use_speechtotext',
                        '⚠️ Mic access blocked. Enable it in your browser\'s site settings to use speech-to-text.',
                      )}
                      onMicClick={() => {
                        onVoiceInputStart?.();
                        editorRef.current?.beginVoice();
                        void stt.start();
                      }}
                    />
                  ) : undefined
                }
                bottomBarActions={
                  stt.state === 'connecting' || stt.state === 'stopping' ? (
                    <CircularLoader size="small" />
                  ) : stt.state === 'recording' ? (
                    <RecordingActions
                      cancelLabel={t('Cancel_recording', 'Cancel recording')}
                      confirmLabel={t('Stop_recording', 'Stop recording')}
                      onConfirm={() => {
                        onVoiceInputConfirm?.();
                        editorRef.current?.endVoice();
                        void stt.stop();
                      }}
                      onCancel={() => {
                        onVoiceInputCancel?.();
                        editorRef.current?.cancelVoice();
                        stt.cancel();
                      }}
                    />
                  ) : undefined
                }
              />
            </footer>
          }
        />
      </>
    );

    const isFloatingOpen = !minimized && imode === 'floating';

    const panel = (
      <div
        ref={panelRef}
        className={classify(css.panel, {
          [css.panelMinimized]: minimized,
          [css.panelMinimizedDragging]: minimized && !!minimizedDragPos,
          [css.panelFloating]: isFloatingOpen,
          [css.panelFullscreen]: imode === 'fullscreen',
        })}
        style={getPanelStyle({
          imode,
          minimized,
          floatingFullHeight,
          height,
          pos,
          hasCustomPosition: hasCustomPositionRef.current,
          panelLeftMargin,
          minimizedY,
          minimizedDragPos,
          elevation,
        })}
      >
        {minimized ? (
          <MinimizedChatButton
            variant="primary"
            tooltipTitle={t('Sense_IQ', 'Sense IQ')}
            onClick={() => {
              if (minimizedDidDragRef.current) {
                minimizedDidDragRef.current = false;
                return;
              }
              const rect = panelRef.current?.getBoundingClientRect();
              const x =
                rect && Number.isFinite(rect.left)
                  ? rect.left
                  : typeof panelLeftMargin === 'number'
                  ? panelLeftMargin
                  : null;
              if (x !== null) {
                setPos((p) => ({...p, x}));
              }
              setIsOpen(true);
            }}
            ariaLabel={t('Open', 'Open')}
          />
        ) : imode === 'fullscreen' ? null : (
          header
        )}

        {isOpen ? body : null}
      </div>
    );

    if (minimized) {
      return createPortal(panel, document.body);
    }
    if (imode === 'fullscreen') {
      return createPortal(
        <div className={css.fullscreenShell}>
          <header
            className={classify(
              css.header,
              css.headerNoDrag,
              css.fullscreenHeader,
            )}
          >
            {headerContent}
          </header>
          <div
            className={classify(css.fullscreenBody, {
              [css.fullscreenBodyCentered]: !hasFullscreenAsset,
            })}
          >
            <section
              className={classify(css.fullscreenAskAiPane, {
                [css.fullscreenAskAiPaneCentered]: !hasFullscreenAsset,
              })}
            >
              {panel}
            </section>
            <section
              className={classify(css.fullscreenGenericPane, {
                [css.fullscreenGenericPaneHidden]: !hasFullscreenAsset,
              })}
            >
              <div
                className={css.fullscreenPortalTarget}
                {...{
                  ['data-askai-fullscreen-portal-target']:
                    'askai-fullscreen-portal',
                }}
              ></div>
            </section>
          </div>
        </div>,
        document.body,
      );
    }
    if (imode === 'floating') {
      return createPortal(panel, document.body);
    }
    if (imode === 'attached' && container) {
      // NOTE:(diwaker) In attached mode, allow host to provide separate containers for header and body
      return (
        <>
          {headerContainer
            ? createPortal(headerContent, headerContainer)
            : null}
          {container ? createPortal(body, container) : null}
        </>
      );
    }
    // Fallback: portal to body so the panel never renders inline
    return createPortal(panel, document.body);
  },
);
